import {
  IDENTITY_CHANGE_STORAGE_KEY,
  IDENTITY_SCOPED_STORAGE_KEYS,
  namespacedStorageKey,
} from '@/constants/storage-keys';
import {
  clearTabOrganizationId,
  setCurrentOrganizationId,
} from '@/lib/current-organization';
import { clearGa4User } from '@/lib/ga4';
import { clearGmailToken } from '@/lib/gmail/token-client';
import { queryClient } from '@/lib/query-client';

let epoch = 0;
let signalCount = 0;

const isBrowser = (): boolean => typeof window !== 'undefined';

// Bumped on every identity change; a request spanning a bump holds an outgoing answer.
export const getIdentityEpoch = (): number => epoch;

// The bare key is what builds before per-organization keys wrote; `key.<org>` is every copy since.
const isIdentityScoped = (storedKey: string): boolean =>
  IDENTITY_SCOPED_STORAGE_KEYS.some((key) => {
    const namespaced = namespacedStorageKey(key);
    return storedKey === namespaced || storedKey.startsWith(`${namespaced}.`);
  });

const storedKeys = (): string[] => {
  const keys: string[] = [];
  try {
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (key !== null) keys.push(key);
    }
  } catch {
    /* site data blocked — nothing readable to clear */
  }
  return keys;
};

const clearIdentityScopedStorage = (): void => {
  if (!isBrowser()) return;
  for (const key of storedKeys().filter(isIdentityScoped)) {
    try {
      window.localStorage.removeItem(key);
    } catch {
      /* quota / private mode — leave this key, still clear the rest */
    }
  }
};

// Drops the outgoing identity from this tab's cache and module state.
export const resetIdentity = (): void => {
  epoch += 1;
  queryClient.clear();
  setCurrentOrganizationId(null);
  clearTabOrganizationId();
  clearGa4User();
  clearGmailToken();
  clearIdentityScopedStorage();
};

// Deliberate session changes only — see the loop note on the listener below.
export const resetIdentityEverywhere = (): void => {
  resetIdentity();
  signalCount += 1;
  try {
    // The token must differ every time: `storage` only fires on a real change.
    window.localStorage.setItem(
      IDENTITY_CHANGE_STORAGE_KEY,
      `${Date.now()}:${signalCount}`,
    );
  } catch {
    /* quota / private mode — this tab is still reset */
  }
};

// Resolved by the parser `assign` uses — `/\t/evil.tld` parses as `//evil.tld`, defeating patterns.
const sameOriginPath = (destination: string): string => {
  try {
    const url = new URL(destination, window.location.origin);
    if (url.origin !== window.location.origin) return '/';
    const path = `${url.pathname}${url.search}${url.hash}`;
    // A leading "//" re-resolves as protocol-relative when `assign` parses it back.
    return path.startsWith('//') ? '/' : path;
  } catch {
    return '/';
  }
};

// Full document load: only a teardown ends the outgoing identity's in-flight mutation callbacks.
export const enterIdentity = (destination: string): void => {
  window.location.assign(sameOriginPath(destination));
};

// The same load in place of this history entry, so Back never returns to a URL that only redirects.
export const enterIdentityInPlace = (destination: string): void => {
  window.location.replace(sameOriginPath(destination));
};

// `storage` fires only in the other tabs, so this never re-enters its own sender.
const onIdentityChanged = (event: StorageEvent): void => {
  if (event.key !== IDENTITY_CHANGE_STORAGE_KEY || event.newValue === null) {
    return;
  }
  resetIdentity();
  // Cache removal never notifies mounted observers — only a reload repaints.
  window.location.reload();
};

window.addEventListener('storage', onIdentityChanged);

// A page restored from the back-forward cache may predate a switch; only a fresh load names the right org.
window.addEventListener('pageshow', (event: PageTransitionEvent) => {
  if (event.persisted) window.location.reload();
});
